import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCalculator,
  FaDownload,
  FaShare,
  FaCopy,
  FaCheck,
  FaChartLine,
  FaGraduationCap,
  FaUser,
  FaIdCard,
  FaAward,
  FaPrint,
  FaFilePdf,
  FaBook,
  FaCalendarAlt,
  FaUniversity,
  FaTrophy,
  FaArrowLeft,
  FaRocket,
  FaBrain,
  FaStar,
  FaFire,
  FaShieldAlt,
  FaCreditCard,
  FaWallet,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
// Import the Footer context
import { useFooter } from "../../context/FooterContext";

const CGPACalculator = () => {
  const { user } = useSelector((state) => state.auth);
  const { setShowFooter } = useFooter(); // Add this line
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState("");
  const [sgpaValues, setSgpaValues] = useState(Array(8).fill(""));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showFullTable, setShowFullTable] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [studentDetails, setStudentDetails] = useState({
    name: user?.name || "",
    usn: user?.usn || "",
    branch: user?.branch || "",
    scheme: "",
    examYear: new Date().getFullYear().toString(),
  });
  const resultRef = useRef(null);

  // Hide footer when component mounts, show when unmounts
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchSchemes();
    if (user) {
      setStudentDetails((prev) => ({
        ...prev,
        name: user.name || "",
        usn: user.usn || "",
        branch: user.branch || "",
      }));
    }
  }, [user]);

  const fetchSchemes = async () => {
    try {
      const response = await axios.get(
        "/api/cgpa/schemes",
      );
      setSchemes(response.data);
      if (response.data.length > 0) {
        setSelectedScheme(response.data[0].scheme);
        setStudentDetails((prev) => ({
          ...prev,
          scheme: response.data[0].scheme,
        }));
      }
    } catch (error) {
      toast.error("Failed to fetch schemes");
    }
  };

  const handleSgpaChange = (index, value) => {
    const newValues = [...sgpaValues];
    newValues[index] = value;
    setSgpaValues(newValues);
  };

  const handleStudentDetailChange = (field, value) => {
    setStudentDetails({
      ...studentDetails,
      [field]: value,
    });
  };

  const getDivision = (cgpa) => {
    const percentage = (cgpa - 0.5) * 10;
    if (percentage >= 70) {
      return {
        name: "Distinction",
        color: "#8b5cf6",
        gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
        icon: <FaTrophy />,
        description: "Outstanding Academic Excellence",
        badge: "Top Performer",
      };
    } else if (percentage >= 60) {
      return {
        name: "First Class",
        color: "#10b981",
        gradient: "linear-gradient(135deg, #10b981, #059669)",
        icon: <FaStar />,
        description: "Very Good Performance",
        badge: "Above Average",
      };
    } else if (percentage >= 50) {
      return {
        name: "Second Class",
        color: "#3b82f6",
        gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
        icon: <FaBrain />,
        description: "Good Performance",
        badge: "Average",
      };
    } else if (percentage >= 35) {
      return {
        name: "Pass Class",
        color: "#f59e0b",
        gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
        icon: <FaChartLine />,
        description: "Satisfactory Performance",
        badge: "Need Improvement",
      };
    } else {
      return {
        name: "Fail",
        color: "#ef4444",
        gradient: "linear-gradient(135deg, #ef4444, #dc2626)",
        icon: <FaFire />,
        description: "Requires Significant Improvement",
        badge: "At Risk",
      };
    }
  };

  const calculateCGPA = async () => {
    if (!selectedScheme) {
      toast.error("Please select a scheme");
      return;
    }

    const hasValue = sgpaValues.some((val) => val !== "");
    if (!hasValue) {
      toast.error("Please enter at least one SGPA value");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "/api/cgpa/calculate",
        {
          sgpaArray: sgpaValues,
          scheme: selectedScheme,
        },
      );
      setResult(response.data);

      await saveCGPA(response.data);
    } catch (error) {
      toast.error("Failed to calculate CGPA");
    } finally {
      setLoading(false);
    }
  };

  const saveCGPA = async (calculatedResult) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/academic/save-cgpa",
        {
          cgpa: calculatedResult.cgpa,
          percentage: calculatedResult.percentage,
          classAwarded: getDivision(parseFloat(calculatedResult.cgpa)).name,
          semesterDetails: calculatedResult.semesterDetails,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("CGPA saved to your academic record!");
    } catch (error) {
      console.error("Failed to save CGPA:", error);
    }
  };

  const resetCalculator = () => {
    setSgpaValues(Array(8).fill(""));
    setResult(null);
  };

  const downloadResult = async () => {
    if (resultRef.current) {
      try {
        const loadingToast = toast.loading("Generating PDF...");

        const actionButtons =
          resultRef.current.querySelectorAll(".action-buttons");
        actionButtons.forEach((btn) => (btn.style.display = "none"));

        await new Promise((resolve) => setTimeout(resolve, 200));

        const element = resultRef.current;
        const canvas = await html2canvas(element, {
          scale: 3,
          backgroundColor: "#ffffff",
          logging: false,
          useCORS: true,
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const imgWidth = 190;
        const pageHeight = 277;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 10;

        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight + 10;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`CGPA_Result_${studentDetails.usn || "student"}.pdf`);

        actionButtons.forEach((btn) => (btn.style.display = "flex"));

        toast.dismiss(loadingToast);
        toast.success("PDF downloaded successfully");
      } catch (error) {
        console.error("Download error:", error);
        toast.error("Failed to download PDF");
      }
    }
  };

  const shareResult = () => {
    if (!result) return;

    const division = getDivision(parseFloat(result.cgpa));
    const text = `📊 VTUGenX CGPA Result

Student: ${studentDetails.name}
USN: ${studentDetails.usn}
Branch: ${studentDetails.branch}
Scheme: ${selectedScheme}

CGPA: ${result.cgpa}
Percentage: ${result.percentage}%
Division: ${division.name}

Semester-wise SGPA:
${result.semesterDetails?.map((sem) => `Sem ${sem.semester}: ${sem.sgpa} (${sem.credits} credits)`).join("\n")}

Calculate your CGPA at VTUGenX!`;

    if (navigator.share) {
      navigator
        .share({
          title: "VTUGenX CGPA Result",
          text: text,
          url: window.location.href,
        })
        .catch(() => copyToClipboard(text));
    } else {
      copyToClipboard(text);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const printResult = () => {
    const printWindow = window.open("", "_blank");
    const resultContent = resultRef.current.cloneNode(true);
    const actionButtons = resultContent.querySelectorAll(".action-buttons");
    actionButtons.forEach((btn) => btn.remove());
    printWindow.document.write(`
      <html>
        <head>
          <title>CGPA Result - ${studentDetails.name}</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; }
            .result-card { max-width: 800px; margin: 0 auto; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background: linear-gradient(135deg, #8b5cf6, #3b82f6); color: white; }
            .header { text-align: center; margin-bottom: 30px; }
            .cgpa { font-size: 48px; color: #8b5cf6; font-weight: bold; }
            @media print {
              body { margin: 0; padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>${resultContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const division = result ? getDivision(parseFloat(result.cgpa)) : null;

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 400, damping: 25 },
    },
  };

  // Responsive styles
  const containerPadding = isMobile ? "1rem" : "2rem";
  const cardPadding = isMobile ? "1rem" : "1.5rem";

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        padding: containerPadding,
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{
          x: [0, -100, 0],
          y: [0, 80, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "350px",
          height: "350px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header with Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <Link
            to="/dashboard"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              color: "#8b5cf6",
              textDecoration: "none",
              fontSize: "0.9rem",
              fontWeight: 500,
              padding: "0.5rem 1rem",
              borderRadius: "10px",
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(139,92,246,0.2)"
                : "rgba(139,92,246,0.1)";
              e.target.style.transform = "translateX(-5px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)";
              e.target.style.transform = "translateX(0)";
            }}
          >
            <FaArrowLeft /> Back to Dashboard
          </Link>

          {/* Status Badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.3rem 0.8rem",
              borderRadius: "20px",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              fontSize: "0.7rem",
            }}
          >
            <FaShieldAlt size={10} /> Secure Calculation
          </div>
        </motion.div>

        {/* Header Title */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
          }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ display: "inline-block", marginBottom: "1rem" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                width: "80px",
                height: "80px",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                boxShadow: "0 20px 40px -12px rgba(139,92,246,0.4)",
              }}
            >
              <FaCalculator size={40} color="white" />
            </div>
          </motion.div>
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "2.8rem",
              fontWeight: "bold",
              marginBottom: "0.5rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            CGPA Calculator
          </h1>
          <p
            style={{
              fontSize: isMobile ? "0.85rem" : "1rem",
              color: getTextSecondary(),
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Calculate your Cumulative Grade Point Average with precision using
            VTU's weighted credit system
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "1.5rem" : "2rem",
          }}
        >
          {/* Input Section - Left Side */}
          <motion.div
            variants={itemVariants}
            style={{
              flex: 1,
              background: isDarkMode
                ? "rgba(30, 30, 45, 0.8)"
                : "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              borderRadius: "32px",
              overflow: "hidden",
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.15)",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            {/* Header - Fixed contrast for light mode */}
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                padding: isMobile ? "1.25rem" : "1.75rem",
                color: "white",
                textAlign: "center",
              }}
            >
              <h2
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.3rem",
                  fontWeight: "bold",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  color: "white",
                }}
              >
                <FaUser /> Student Information
              </h2>
              <p
                style={{
                  fontSize: "0.7rem",
                  opacity: 0.9,
                  marginTop: "0.3rem",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                Enter your details for personalized results
              </p>
            </div>

            <div style={{ padding: cardPadding }}>
              <div style={{ marginBottom: "1.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.4rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  <FaUser style={{ marginRight: "0.4rem", color: "#8b5cf6" }} />
                  Student Name
                </label>
                <input
                  type="text"
                  value={studentDetails.name}
                  onChange={(e) =>
                    handleStudentDetailChange("name", e.target.value)
                  }
                  placeholder="Enter your full name"
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    fontSize: "0.9rem",
                    outline: "none",
                    transition: "all 0.3s",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                />
              </div>

              <div style={{ marginBottom: "1.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.4rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  <FaIdCard
                    style={{ marginRight: "0.4rem", color: "#8b5cf6" }}
                  />
                  USN
                </label>
                <input
                  type="text"
                  value={studentDetails.usn}
                  onChange={(e) =>
                    handleStudentDetailChange("usn", e.target.value)
                  }
                  placeholder="e.g., 1BM21CS001"
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    fontSize: "0.9rem",
                    outline: "none",
                    transition: "all 0.3s",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                />
              </div>

              <div style={{ marginBottom: "1.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.4rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  <FaUniversity
                    style={{ marginRight: "0.4rem", color: "#8b5cf6" }}
                  />
                  Branch
                </label>
                <select
                  value={studentDetails.branch}
                  onChange={(e) =>
                    handleStudentDetailChange("branch", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    fontSize: "0.9rem",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select Branch</option>
                  <option value="CSE">Computer Science Engineering</option>
                  <option value="ISE">Information Science Engineering</option>
                  <option value="ECE">Electronics & Communication</option>
                  <option value="EEE">Electrical & Electronics</option>
                  <option value="MECH">Mechanical Engineering</option>
                  <option value="CIVIL">Civil Engineering</option>
                </select>
              </div>

              <div style={{ marginBottom: "1.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.4rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  <FaGraduationCap
                    style={{ marginRight: "0.4rem", color: "#8b5cf6" }}
                  />
                  Academic Scheme
                </label>
                <select
                  value={selectedScheme}
                  onChange={(e) => setSelectedScheme(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    fontSize: "0.9rem",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  {schemes.map((scheme) => (
                    <option key={scheme.scheme} value={scheme.scheme}>
                      {scheme.name} ({scheme.scheme})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: "1.25rem" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "0.75rem",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                  }}
                >
                  <label
                    style={{
                      color: getTextSecondary(),
                      fontWeight: "500",
                      fontSize: "0.85rem",
                    }}
                  >
                    <FaBook
                      style={{ marginRight: "0.4rem", color: "#8b5cf6" }}
                    />
                    Semester SGPA Values
                  </label>
                  <button
                    onClick={() => setShowFullTable(!showFullTable)}
                    style={{
                      padding: "0.25rem 0.75rem",
                      background: isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "rgba(139,92,246,0.05)",
                      border: `1px solid ${getBorderColor()}`,
                      borderRadius: "20px",
                      fontSize: "0.7rem",
                      cursor: "pointer",
                      color: "#8b5cf6",
                    }}
                  >
                    {showFullTable ? "Show Less" : "Show All 8 Semesters"}
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: showFullTable
                      ? isMobile
                        ? "repeat(2, 1fr)"
                        : "repeat(4, 1fr)"
                      : "repeat(2, 1fr)",
                    gap: "0.75rem",
                  }}
                >
                  {sgpaValues.map((value, index) => (
                    <div key={index}>
                      <label
                        style={{
                          fontSize: "0.7rem",
                          color: getTextSecondary(),
                          marginBottom: "0.25rem",
                          display: "block",
                          textAlign: "center",
                        }}
                      >
                        Semester {index + 1}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="10"
                        value={value}
                        onChange={(e) =>
                          handleSgpaChange(index, e.target.value)
                        }
                        placeholder="SGPA"
                        style={{
                          width: "100%",
                          padding: "0.6rem",
                          border: `2px solid ${getBorderColor()}`,
                          borderRadius: "12px",
                          textAlign: "center",
                          fontSize: "0.85rem",
                          outline: "none",
                          background: getInputBackground(),
                          color: getTextColor(),
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#8b5cf6")
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  flexWrap: "wrap",
                  marginTop: "0.5rem",
                }}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={calculateCGPA}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "0.85rem",
                    background: loading
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "16px",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    cursor: loading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    transition: "all 0.3s",
                    boxShadow: loading
                      ? "none"
                      : "0 4px 14px 0 rgba(139,92,246,0.4)",
                  }}
                >
                  <FaCalculator />{" "}
                  {loading ? "Calculating..." : "Calculate CGPA"}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={resetCalculator}
                  style={{
                    padding: "0.85rem 1.5rem",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)",
                    color: getTextSecondary(),
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "16px",
                    cursor: "pointer",
                    fontWeight: "500",
                    transition: "all 0.3s",
                    fontSize: "0.9rem",
                  }}
                >
                  Reset
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Result Section - Right Side */}
          <AnimatePresence>
            {result && result.cgpa > 0 && (
              <motion.div
                ref={resultRef}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, x: 20 }}
                style={{
                  flex: 1,
                  background: isDarkMode
                    ? "rgba(30, 30, 45, 0.8)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "32px",
                  overflow: "hidden",
                  boxShadow: isDarkMode
                    ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                    : "0 25px 50px -12px rgba(0,0,0,0.15)",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                {/* Header - Fixed contrast for light mode */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                    padding: isMobile ? "1.25rem" : "1.75rem",
                    color: "white",
                  }}
                >
                  <h3
                    style={{
                      fontSize: isMobile ? "1rem" : "1.2rem",
                      fontWeight: "bold",
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      color: "white",
                    }}
                  >
                    <FaWallet /> CGPA Grade Card
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                      gap: "0.8rem",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "0.65rem",
                          opacity: 0.8,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      >
                        Student Name
                      </div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        {studentDetails.name || "Not Specified"}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.65rem",
                          opacity: 0.8,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      >
                        USN
                      </div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        {studentDetails.usn || "Not Specified"}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.65rem",
                          opacity: 0.8,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      >
                        Branch
                      </div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        {studentDetails.branch || "Not Specified"}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.65rem",
                          opacity: 0.8,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      >
                        Scheme
                      </div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        {selectedScheme}
                      </div>
                    </div>
                  </div>
                </div>

                {/* CGPA and Percentage - Main Result */}
                <div
                  style={{
                    padding: isMobile ? "1.5rem" : "2rem",
                    textAlign: "center",
                    borderBottom: `1px solid ${getBorderColor()}`,
                  }}
                >
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    style={{
                      fontSize: isMobile ? "3.5rem" : "4.5rem",
                      fontWeight: "bold",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {result.cgpa}
                  </motion.div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: getTextSecondary(),
                      marginBottom: "1rem",
                    }}
                  >
                    CGPA (out of 10)
                  </div>

                  <div
                    style={{
                      fontSize: isMobile ? "1.8rem" : "2rem",
                      fontWeight: "bold",
                      color: getTextColor(),
                      marginBottom: "1rem",
                    }}
                  >
                    {result.percentage}%
                  </div>

                  {division && (
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: isMobile ? "0.6rem 1.5rem" : "0.75rem 2rem",
                        borderRadius: "40px",
                        background: division.gradient,
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {division.icon}
                      {division.name}
                    </motion.div>
                  )}

                  {division && (
                    <p
                      style={{
                        color: getTextSecondary(),
                        fontSize: "0.7rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      {division.description}
                    </p>
                  )}

                  <div
                    style={{
                      background: isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "rgba(139,92,246,0.05)",
                      borderRadius: "16px",
                      padding: "0.8rem",
                      marginTop: "1.5rem",
                      display: "inline-block",
                      border: `1px solid ${getBorderColor()}`,
                    }}
                  >
                    <p
                      style={{
                        color: getTextSecondary(),
                        fontSize: "0.75rem",
                        fontFamily: "monospace",
                        margin: 0,
                      }}
                    >
                      {result.totalPoints} ÷ {result.totalCredits} ={" "}
                      <strong style={{ color: "#8b5cf6" }}>
                        {result.cgpa}
                      </strong>
                    </p>
                  </div>
                </div>

                {/* Semester Details Table */}
                <div style={{ padding: cardPadding }}>
                  <h4
                    style={{
                      marginBottom: "1rem",
                      color: getTextColor(),
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FaBook size={14} color="#8b5cf6" /> Semester Performance
                  </h4>
                  <div style={{ overflowX: "auto" }}>
                    {result.semesterDetails?.map((sem, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.75rem 0",
                          borderBottom: `1px solid ${getBorderColor()}`,
                        }}
                      >
                        <span
                          style={{
                            color: getTextSecondary(),
                            fontSize: "0.85rem",
                          }}
                        >
                          Semester {sem.semester}
                        </span>
                        <span
                          style={{
                            fontWeight: "bold",
                            color: "#8b5cf6",
                            fontSize: "0.9rem",
                          }}
                        >
                          {sem.sgpa}
                        </span>
                        <span
                          style={{
                            color: getTextSecondary(),
                            fontSize: "0.7rem",
                          }}
                        >
                          {sem.credits} credits
                        </span>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      marginTop: "1rem",
                      padding: "0.8rem",
                      background: isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "rgba(139,92,246,0.05)",
                      borderRadius: "16px",
                      textAlign: "center",
                      border: `1px solid ${getBorderColor()}`,
                    }}
                  >
                    <strong
                      style={{ color: getTextColor(), fontSize: "0.85rem" }}
                    >
                      Total Credits Earned: {result.totalCredits}
                    </strong>
                  </div>
                </div>

                {/* Action Buttons */}
                <div
                  className="action-buttons"
                  style={{
                    padding: "1rem",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.05)"
                      : "rgba(139,92,246,0.03)",
                    display: "flex",
                    gap: "0.75rem",
                    borderTop: `1px solid ${getBorderColor()}`,
                    flexWrap: "wrap",
                  }}
                >
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={downloadResult}
                    style={{
                      flex: 1,
                      padding: "0.6rem",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      fontSize: "0.8rem",
                      fontWeight: "500",
                    }}
                  >
                    <FaFilePdf size={14} /> Download PDF
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={printResult}
                    style={{
                      flex: 1,
                      padding: "0.6rem",
                      background: isDarkMode
                        ? "rgba(100,116,139,0.3)"
                        : "#e2e8f0",
                      color: getTextColor(),
                      border: "none",
                      borderRadius: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      fontSize: "0.8rem",
                      fontWeight: "500",
                    }}
                  >
                    <FaPrint size={14} /> Print
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={shareResult}
                    style={{
                      flex: 1,
                      padding: "0.6rem",
                      background: "linear-gradient(135deg, #10b981, #059669)",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      fontSize: "0.8rem",
                      fontWeight: "500",
                    }}
                  >
                    {copied ? <FaCheck size={14} /> : <FaShare size={14} />}
                    {copied ? "Copied!" : "Share"}
                  </motion.button>
                </div>

                {/* Footer Note */}
                <div
                  style={{
                    padding: "0.75rem",
                    textAlign: "center",
                    fontSize: "0.6rem",
                    color: getTextSecondary(),
                    borderTop: `1px solid ${getBorderColor()}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FaShieldAlt size={10} />
                  This is a computer-generated grade card. Valid only with
                  official university stamp.
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default CGPACalculator;

